import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { projects } from '../data/projects'
import { skills } from '../data/skills'
import { Icon } from './Icon'
import { Text } from './Text'
import { ProjectCard } from './ProjectCard'
import { ContactForm } from './ContactForm'

const PortfolioContainer = styled(motion.div)`
  min-height: 100vh;
  width: 100%;
  background-color: #0a0a0a;
  color: #ffffff;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    sans-serif;
  overflow-x: hidden;
`

const HeroSection = styled(motion.section)`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 2rem;
  position: relative;
`

const Title = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #4ecdc4, #ff6b6b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 20px rgba(78, 205, 196, 0.3);

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`

const Subtitle = styled(motion.p)`
  font-size: 1.8rem;
  color: #ffffff;
  margin-bottom: 2rem;
  max-width: 600px;

  @media (max-width: 768px) {
    font-size: 1.4rem;
  }
`

const Description = styled(motion.p)`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  max-width: 800px;
  line-height: 1.8;
  margin-bottom: 3rem;
`

const Section = styled(motion.section)`
  width: 100%;
  max-width: 1200px;
  padding: 4rem 2rem;
  margin: 0 auto;
`

const SectionTitle = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 2rem;
  text-align: center;
  background: linear-gradient(45deg, #4ecdc4, #ff6b6b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`

const ProjectsSection = styled(Section)`
  position: relative;
  overflow: hidden;
`

const ProjectsCarousel = styled(motion.div)`
  display: flex;
  gap: 2rem;
  transition: transform 0.3s ease;
  padding: 0 2rem;
  margin-bottom: 2rem;
`

const CarouselControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
`

const CarouselButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #ffffff;
  font-size: 1.2rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`

const CarouselDots = styled.div`
  display: flex;
  gap: 0.5rem;
  margin: 0 1rem;
`

const CarouselDot = styled.button<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  background: ${(props) =>
    props.$active ? '#4ecdc4' : 'rgba(255, 255, 255, 0.3)'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${(props) =>
      props.$active ? '#4ecdc4' : 'rgba(255, 255, 255, 0.5)'};
  }
`

const SkillsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    padding: 0 1rem;
  }
`

const SkillCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1.5rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    background: rgba(255, 255, 255, 0.08);
  }
`

const SkillHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const SkillTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const SkillBadges = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const SkillLevel = styled.div<{ $level: string }>`
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.9rem;
  display: inline-block;
  background: ${(props) => {
    switch (props.$level) {
      case 'Expert':
        return 'rgba(78, 205, 196, 0.1)'
      case 'Advanced':
        return 'rgba(255, 171, 0, 0.1)'
      case 'Intermediate':
        return 'rgba(255, 107, 107, 0.1)'
      default:
        return 'rgba(255, 255, 255, 0.1)'
    }
  }};
  color: ${(props) => {
    switch (props.$level) {
      case 'Expert':
        return '#4ECDC4'
      case 'Advanced':
        return '#FFAB00'
      case 'Intermediate':
        return '#ff6b6b'
      default:
        return '#ffffff'
    }
  }};
`

const ExperienceBadge = styled.div`
  padding: 0.3rem 0.8rem;
  border-radius: 15px;
  font-size: 0.9rem;
  background: rgba(255, 255, 255, 0.1);
  color: #ffffff;
  display: inline-block;

  &::after {
    content: 'years';
    font-size: 0.8rem;
    opacity: 0.7;
    margin-left: 0.3rem;
  }
`

const ContactSection = styled(Section)`
  text-align: center;
`

const ContactButton = styled(motion.button)`
  background: linear-gradient(45deg, #4ecdc4, #ff6b6b);
  border: none;
  padding: 1rem 2rem;
  border-radius: 25px;
  color: white;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(78, 205, 196, 0.3);
  }
`

const ViewAllButton = styled(ContactButton)`
  margin: 2rem auto;
  display: block;
  width: auto;
  min-width: 200px;
`

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
}

// Color map for skill icons
const skillIconColorMap: Record<string, string> = {
  javascript: '#f7df1e',
  typescript: '#3178c6',
  react: '#61dafb',
  node: '#3c873a',
  mongodb: '#47a248',
  aws: '#ff9900',
  git: '#f34f29',
  docker: '#2496ed',
  graphql: '#e535ab',
}

export function Portfolio() {
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(0)
  const featuredProjects = projects.filter((project) => project.featured)
  const featuredSkills = skills.filter((skill) => skill.featured).slice(0, 6)
  const projectsPerPage = 3
  const totalPages = Math.ceil(featuredProjects.length / projectsPerPage)

  const handlePrevPage = () => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : totalPages - 1))
  }

  const handleNextPage = () => {
    setCurrentPage((prev) => (prev < totalPages - 1 ? prev + 1 : 0))
  }

  const handleDotClick = (index: number) => {
    setCurrentPage(index)
  }

  const handleContactSuccess = () => {
    console.log('Contact form submitted successfully')
  }

  const visibleProjects = featuredProjects.slice(
    currentPage * projectsPerPage,
    (currentPage + 1) * projectsPerPage
  )

  const handleProjectClick = (projectId: string) => {
    navigate(`/projects#${projectId}`)
  }

  return (
    <PortfolioContainer>
      <HeroSection
        variants={containerVariants}
        initial='hidden'
        animate='visible'
        id='hero'
        aria-labelledby='hero-title'>
        <Title id='hero-title' variants={itemVariants}>
          Hi, I'm Dzirmal
        </Title>
        <Subtitle variants={itemVariants}>
          Full Stack Developer & UI/UX Enthusiast
        </Subtitle>
        <Description variants={itemVariants}>
          I specialize in creating modern, responsive web applications with a
          focus on user experience and clean code. With expertise in both
          frontend and backend development, I bring ideas to life through
          elegant solutions and innovative design.
        </Description>
        <ContactButton
          onClick={() => navigate('/contact')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          role='button'
          aria-label='Get in touch'>
          Get in Touch →
        </ContactButton>
      </HeroSection>

      <ProjectsSection
        variants={containerVariants}
        initial='hidden'
        whileInView='visible'
        viewport={{ once: true, margin: '-100px' }}
        id='projects'
        aria-labelledby='projects-title'>
        <SectionTitle id='projects-title' variants={itemVariants}>
          Featured Projects
        </SectionTitle>
        <ProjectsCarousel
          variants={containerVariants}
          initial='hidden'
          animate='visible'
          style={{
            transform: `translateX(-${currentPage * 100}%)`,
          }}>
          {visibleProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              variant='minimal'
              onClick={() => handleProjectClick(project.id)}
            />
          ))}
        </ProjectsCarousel>
        <CarouselControls>
          <CarouselButton
            onClick={handlePrevPage}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            aria-label='Previous projects'>
            ←
          </CarouselButton>
          <CarouselDots>
            {Array.from({ length: totalPages }).map((_, index) => (
              <CarouselDot
                key={index}
                $active={currentPage === index}
                onClick={() => handleDotClick(index)}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </CarouselDots>
          <CarouselButton
            onClick={handleNextPage}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            aria-label='Next projects'>
            →
          </CarouselButton>
        </CarouselControls>
        <ViewAllButton
          onClick={() => navigate('/projects')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          role='button'
          aria-label='View all projects'>
          View All Projects →
        </ViewAllButton>
      </ProjectsSection>

      <Section
        variants={containerVariants}
        initial='hidden'
        whileInView='visible'
        viewport={{ once: true, margin: '-100px' }}
        id='skills'
        aria-labelledby='skills-title'>
        <SectionTitle id='skills-title' variants={itemVariants}>
          Skills & Expertise
        </SectionTitle>
        <SkillsGrid>
          {featuredSkills.map((skill) => (
            <SkillCard
              key={skill.id}
              whileHover={{ y: -5 }}
              transition={{ duration: 0.2 }}>
              <SkillHeader>
                <SkillTitle>
                  <Icon
                    icon={skill.icon}
                    size={32}
                    color={skillIconColorMap[skill.id] || '#fff'}
                  />
                  <Text
                    text={skill.name}
                    fontSize='1.2rem'
                    fontWeight='bold'
                    color='#ffffff'
                    margin='0'
                    role='heading'
                    aria-level={4}
                  />
                </SkillTitle>
              </SkillHeader>
              <SkillBadges>
                <SkillLevel $level={skill.level}>{skill.level}</SkillLevel>
                <ExperienceBadge>{skill.yearsOfExperience}</ExperienceBadge>
              </SkillBadges>
            </SkillCard>
          ))}
        </SkillsGrid>
        <ViewAllButton
          onClick={() => navigate('/skills')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          role='button'
          aria-label='View all skills'>
          View All Skills →
        </ViewAllButton>
      </Section>

      <ContactSection
        variants={containerVariants}
        initial='hidden'
        whileInView='visible'
        viewport={{ once: true, margin: '-100px' }}
        id='contact'
        aria-labelledby='contact-title'>
        <SectionTitle id='contact-title' variants={itemVariants}>
          Get in Touch
        </SectionTitle>
        <Text
          text="I'm always open to discussing new opportunities, interesting projects, and potential collaborations."
          fontSize='1.2rem'
          color='#a0a0a0'
          margin='0 0 2rem 0'
          role='doc-subtitle'
        />
        <ContactForm onSuccess={handleContactSuccess} />
      </ContactSection>
    </PortfolioContainer>
  )
}
